import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, CheckCircle2, Gauge } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { editBlockReason, setAreaDone, setGuns } from '@/data/nights';
import { rt } from '@/data/runtime';
import { addDays, formatBusinessDate, isBusinessDate } from '@/domain/businessDate';
import { Button, Card, EmptyState, Notice, Spinner } from '@/ui/core';
import { NumberInput } from '@/ui/form';
import { useToast } from '@/ui/overlay';
import { ButtonLink } from '@/ui/ButtonLink';
import { useSession } from '@/app/providers';

/** The two liquor gun meter readings at close (the "Closing" rows under each gun). */
export function GunsPage() {
  const { date = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { actor } = useSession();
  const loaded = useLiveQuery(async () => ({ night: await rt().db.nights.get(date), before: await rt().db.nights.get(addDays(date, -1)) }), [date]);
  const [g1, setG1] = useState<number | null>(null);
  const [g2, setG2] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!loaded || ready) return;
    setG1(loaded.night?.gun1 ?? null);
    setG2(loaded.night?.gun2 ?? null);
    setReady(true);
  }, [loaded, ready]);

  if (!isBusinessDate(date)) return <EmptyState title="That page doesn’t exist" action={<ButtonLink to="/">Home</ButtonLink>} />;
  if (!loaded || !ready) return <Spinner />;
  const { night, before } = loaded;
  const blocked = editBlockReason(night, 'guns', undefined, actor.role);
  const done = !!night?.areasDone.guns;
  const canReopen = night?.status === 'counting' || (night?.status === 'finished' && actor.role !== 'staff');
  const prev1 = night?.openings.gun1 ?? before?.gun1 ?? null;
  const prev2 = night?.openings.gun2 ?? before?.gun2 ?? null;
  const low1 = g1 != null && prev1 != null && g1 < prev1;
  const low2 = g2 != null && prev2 != null && g2 < prev2;
  const big1 = g1 != null && prev1 != null && g1 - prev1 > 2000;
  const big2 = g2 != null && prev2 != null && g2 - prev2 > 2000;

  const save = async () => {
    setBusy(true);
    try {
      await setGuns(date, g1, g2);
      await setAreaDone(date, 'guns', true);
      toast.show('Gun readings saved. Thank you!');
      navigate('/');
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  };

  const reading = (n: 1 | 2, value: number | null, set: (v: number | null) => void, prev: number | null, low: boolean, big: boolean) => (
    <Card className="flex flex-col gap-3">
      <h2 className="flex items-center gap-2 text-2xl font-bold">
        <Gauge className="size-7 text-accent" aria-hidden /> Liquor gun {n}
      </h2>
      <label htmlFor={`gun-${n}`} className="text-base font-semibold">
        The number on the meter now
      </label>
      <NumberInput id={`gun-${n}`} aria-label={`Liquor gun ${n}: the number on the meter now`} value={value} onValueChange={set} decimals disabled={!!blocked || done} size="xl" align="center" placeholder="—" className="max-w-xs" />
      <p className="text-base text-muted">{prev != null ? `Last reading: ${prev.toLocaleString('en-CA')}` : 'No earlier reading in the app yet.'}</p>
      {low && (
        <Notice tone="warn" icon={<AlertTriangle className="size-5" aria-hidden />}>
          That’s lower than the last reading. A meter only goes up — check the number.
        </Notice>
      )}
      {big && (
        <Notice tone="warn" icon={<AlertTriangle className="size-5" aria-hidden />}>
          That’s over 2,000 more than the last reading. Check the number.
        </Notice>
      )}
    </Card>
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div>
        <p className="text-base text-muted md:text-lg">{formatBusinessDate(date, 'en-CA', 'long')}</p>
        <h1 className="flex items-center gap-1 text-3xl font-bold md:text-4xl">
          Liquor guns <HelpBubble topic="liquor-guns" />
        </h1>
      </div>
      {blocked && !done && <Notice tone="info">{blocked}</Notice>}
      {done && (
        <Notice
          tone="ok"
          icon={<CheckCircle2 className="size-5" aria-hidden />}
          title="Done"
          action={
            canReopen ? (
              <Button
                onClick={async () => {
                  try {
                    await setAreaDone(date, 'guns', false);
                  } catch (err) {
                    toast.error(err);
                  }
                }}
              >
                Change something
              </Button>
            ) : undefined
          }
        >
          The readings are saved.
        </Notice>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {reading(1, g1, setG1, prev1, low1, big1)}
        {reading(2, g2, setG2, prev2, low2, big2)}
      </div>
      {!blocked && !done ? (
        <Button size="xl" variant="ok" busy={busy} disabled={g1 == null || g2 == null} onClick={save} icon={<CheckCircle2 className="size-6" aria-hidden />}>
          Save both readings
        </Button>
      ) : (
        <ButtonLink to="/" size="xl" variant="primary" block>
          Back to the home screen
        </ButtonLink>
      )}
    </div>
  );
}
